
import { supabase } from '@/integrations/supabase/client';
import { SecureCredentialsService } from './secureCredentialsService';
import { TokenRefreshService } from './tokenRefreshService';

export interface EmailMessage {
  id: string;
  subject: string;
  from: {
    emailAddress: {
      address: string;
      name: string;
    };
  };
  toRecipients: Array<{
    emailAddress: {
      address: string;
      name: string;
    };
  }>;
  receivedDateTime: string;
  bodyPreview: string;
  body: {
    content: string;
    contentType: string;
  };
  importance: string;
  isRead: boolean;
  hasAttachments: boolean;
  conversationId: string;
}

export interface CalendarEvent {
  id: string;
  subject: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: {
    displayName: string;
  };
  attendees?: Array<{
    emailAddress: {
      address: string;
      name: string;
    };
  }>;
  body?: {
    content: string;
    contentType: string;
  };
}

export interface UserProfile {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
}

export interface ListEmailsOptions {
  limit?: number;
  filter?: string;
  orderBy?: string;
  select?: string[];
}

export interface ListCalendarEventsOptions {
  startTime: string;
  endTime: string;
  limit?: number;
}

export class MicrosoftGraphService {
  private static readonly GRAPH_API_BASE = 'https://graph.microsoft.com/v1.0';

  /**
   * Get a valid access token for the user, refreshing if necessary
   */
  private static async getValidAccessToken(userId: string): Promise<string> {
    // Try to refresh tokens if needed
    const refreshResult = await TokenRefreshService.refreshTokensIfNeeded(userId);
    
    if (!refreshResult.success) {
      if (refreshResult.needsReauth) {
        throw new Error('User needs to re-authenticate with Microsoft');
      }
      throw new Error(refreshResult.error || 'Failed to refresh tokens');
    }

    // Get the current (now refreshed) credentials
    const credentials = await SecureCredentialsService.getCredentials(userId);
    if (!credentials) {
      throw new Error('No valid credentials found');
    }

    return credentials.access_token;
  }

  /**
   * Make an authenticated request to Microsoft Graph API
   */
  private static async makeGraphRequest<T>(
    userId: string,
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const accessToken = await this.getValidAccessToken(userId);

    const response = await fetch(`${this.GRAPH_API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Graph API request failed: ${error.error?.message || response.statusText}`);
    }

    return response.json();
  }

  /**
   * List emails from user's mailbox
   */
  static async listEmails(userId: string, options: ListEmailsOptions = {}): Promise<EmailMessage[]> {
    const {
      limit = 50,
      filter,
      orderBy = 'receivedDateTime desc',
      select = ['id', 'subject', 'from', 'toRecipients', 'receivedDateTime', 'bodyPreview', 'body', 'importance', 'isRead', 'hasAttachments', 'conversationId']
    } = options;

    let endpoint = `/me/messages?$top=${limit}&$orderby=${orderBy}&$select=${select.join(',')}`;
    
    if (filter) {
      endpoint += `&$filter=${encodeURIComponent(filter)}`;
    }

    const response = await this.makeGraphRequest<{ value: EmailMessage[] }>(userId, endpoint);
    return response.value;
  }

  /**
   * Get a specific email by ID
   */
  static async getEmail(userId: string, emailId: string): Promise<EmailMessage> {
    return this.makeGraphRequest<EmailMessage>(userId, `/me/messages/${emailId}`);
  }

  /**
   * List calendar events
   */
  static async listCalendarEvents(userId: string, options: ListCalendarEventsOptions): Promise<CalendarEvent[]> {
    const { startTime, endTime, limit = 50 } = options;
    
    const endpoint = `/me/calendar/events?$top=${limit}&$filter=start/dateTime ge '${startTime}' and end/dateTime le '${endTime}'&$orderby=start/dateTime`;
    
    const response = await this.makeGraphRequest<{ value: CalendarEvent[] }>(userId, endpoint);
    return response.value;
  }

  /**
   * Get user profile information
   */
  static async getUserProfile(userId: string): Promise<UserProfile> {
    return this.makeGraphRequest<UserProfile>(userId, '/me');
  }

  /**
   * Send an email
   */
  static async sendEmail(userId: string, message: {
    subject: string;
    body: string;
    toRecipients: string[];
    ccRecipients?: string[];
    bccRecipients?: string[];
  }): Promise<void> {
    const emailData = {
      message: {
        subject: message.subject,
        body: {
          contentType: 'HTML',
          content: message.body,
        },
        toRecipients: message.toRecipients.map(email => ({
          emailAddress: {
            address: email,
          },
        })),
        ccRecipients: message.ccRecipients?.map(email => ({
          emailAddress: {
            address: email,
          },
        })) || [],
        bccRecipients: message.bccRecipients?.map(email => ({
          emailAddress: {
            address: email,
          },
        })) || [],
      },
      saveToSentItems: true,
    };

    await this.makeGraphRequest(userId, '/me/sendMail', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  /**
   * Create a calendar event
   */
  static async createCalendarEvent(userId: string, event: {
    subject: string;
    start: string;
    end: string;
    timeZone?: string;
    location?: string;
    attendees?: string[];
    body?: string;
  }): Promise<CalendarEvent> {
    const eventData = {
      subject: event.subject,
      start: {
        dateTime: event.start,
        timeZone: event.timeZone || 'UTC',
      },
      end: {
        dateTime: event.end,
        timeZone: event.timeZone || 'UTC',
      },
      location: event.location ? {
        displayName: event.location,
      } : undefined,
      attendees: event.attendees?.map(email => ({
        emailAddress: {
          address: email,
        },
      })) || [],
      body: event.body ? {
        contentType: 'HTML',
        content: event.body,
      } : undefined,
    };

    return this.makeGraphRequest<CalendarEvent>(userId, '/me/calendar/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  /**
   * Get delta changes for emails (for efficient sync)
   */
  static async getEmailDeltas(userId: string, deltaToken?: string): Promise<{
    messages: EmailMessage[];
    deltaToken: string;
    hasMore: boolean;
  }> {
    let endpoint = '/me/mailFolders/inbox/messages/delta?$select=id,subject,from,toRecipients,receivedDateTime,bodyPreview,body,importance,isRead,hasAttachments,conversationId';
    
    if (deltaToken) {
      endpoint = deltaToken; // Delta token contains the full URL
    }

    const response = await this.makeGraphRequest<{
      value: EmailMessage[];
      '@odata.nextLink'?: string;
      '@odata.deltaLink'?: string;
    }>(userId, endpoint.replace(this.GRAPH_API_BASE, ''));

    return {
      messages: response.value,
      deltaToken: response['@odata.deltaLink'] || response['@odata.nextLink'] || '',
      hasMore: !!response['@odata.nextLink'],
    };
  }
}
